import React, { useState } from 'react';
import axios from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';

const CloseJobModal = ({ job, onClose, onSuccess }) => {
  const [totalSpent, setTotalSpent] = useState('');
  const [closedDate, setClosedDate] = useState(new Date().toISOString().split('T')[0]);
  const [closedApplicantsCount, setClosedApplicantsCount] = useState('');
  const [closedApplicantsScreenshot, setClosedApplicantsScreenshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      if (totalSpent !== '') {
        formData.append('totalSpent', totalSpent);
      }
      formData.append('closedDate', closedDate);
      formData.append('closedApplicantsCount', closedApplicantsCount);
      if (closedApplicantsScreenshot) {
        formData.append('closedApplicantsScreenshot', closedApplicantsScreenshot);
      }

      await axios.post(`/api/jobs/${job.id}/close`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Error closing job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Close Job</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Closing <span className="font-medium">{job.position} — {job.title}</span>
        </p>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Spent on Job
            </label>
            <input
              type="number"
              step="0.01"
              value={totalSpent}
              onChange={(e) => setTotalSpent(e.target.value)}
              placeholder="e.g. 50000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date Closed <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={closedDate}
              onChange={(e) => setClosedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Applicants <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="1"
              required
              value={closedApplicantsCount}
              onChange={(e) => setClosedApplicantsCount(e.target.value)}
              placeholder="e.g. 120"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Closed Job Screenshot (showing applicants) <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
              required
              onChange={(e) => setClosedApplicantsScreenshot(e.target.files?.[0] || null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            {closedApplicantsScreenshot && (
              <p className="mt-1 text-xs text-gray-500">{closedApplicantsScreenshot.name}</p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Closing...' : 'Close Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CloseJobModal;
